const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 24 * 60 * 60 * 1000;
const WEEKDAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
/** True for a well-formed, real calendar date (rejects 2026-02-30). */
export function isIsoDate(value) {
    if (typeof value !== 'string' || !ISO_DATE.test(value))
        return false;
    const date = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}
/** Midnight UTC for a calendar date. */
export function toUtcDate(iso) {
    if (!isIsoDate(iso))
        throw new Error(`Invalid ISO date: ${iso}`);
    return new Date(`${iso}T00:00:00Z`);
}
/** Calendar date for a UTC instant. */
export function formatIsoDate(date) {
    return date.toISOString().slice(0, 10);
}
/** Today's calendar date in a given IANA timezone (the hotel's). */
export function todayInTimeZone(timeZone, now = new Date()) {
    // en-CA formats as YYYY-MM-DD.
    return new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(now);
}
/** Hour of the day (0-23) in a given IANA timezone. */
export function hourInTimeZone(timeZone, now = new Date()) {
    const hour = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour: 'numeric',
        hourCycle: 'h23',
    }).format(now);
    // Some ICU builds print midnight as "24" under h23; fold it back.
    return Number(hour) % 24;
}
export function addDays(iso, days) {
    const date = toUtcDate(iso);
    date.setUTCDate(date.getUTCDate() + days);
    return formatIsoDate(date);
}
/** Whole nights between check-in and check-out; 0 when check-out is not after check-in. */
export function nightsBetween(checkIn, checkOut) {
    const nights = Math.round((toUtcDate(checkOut).getTime() - toUtcDate(checkIn).getTime()) / DAY_MS);
    return Math.max(0, nights);
}
/** Every night of a stay: check-in through the night before check-out. */
export function eachNight(checkIn, checkOut) {
    const nights = [];
    for (let i = 0; i < nightsBetween(checkIn, checkOut); i += 1) {
        nights.push(addDays(checkIn, i));
    }
    return nights;
}
/** Day-of-week key for a calendar date (used for per-weekday rate overrides). */
export function weekdayKey(iso) {
    const key = WEEKDAYS[toUtcDate(iso).getUTCDay()];
    if (!key)
        throw new Error(`Invalid weekday for ${iso}`);
    return key;
}
/** -1 when a is before b, 0 when equal, 1 when after. */
export function compareIsoDates(a, b) {
    if (a === b)
        return 0;
    return a < b ? -1 : 1;
}
/** "Fri, Oct 16, 2026" style label. Timezone-safe because the date is treated as UTC. */
export function formatStayDate(iso, options = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
}, locale = 'en-US') {
    return new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' }).format(toUtcDate(iso));
}
/**
 * "15:00" -> "3:00 PM", "12:00" -> "12:00 PM", "00:30" -> "12:30 AM". Hotel
 * check-in and check-out times are stored as 24-hour strings and shown to
 * guests the American way.
 */
export function formatClockTime(value) {
    const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
    if (!match)
        return value;
    const hours = Number(match[1]);
    const minutes = match[2];
    if (hours > 23 || Number(minutes) > 59)
        return value;
    const suffix = hours >= 12 ? 'PM' : 'AM';
    const twelve = hours % 12 === 0 ? 12 : hours % 12;
    return `${twelve}:${minutes} ${suffix}`;
}
//# sourceMappingURL=dates.js.map